import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, Button, Chip, IconButton,
  Tooltip, Alert, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, CircularProgress,
} from '@mui/material';
import {
  VerifiedUser, Upload, CheckCircle, Cancel, Visibility,
  Close, Description, Delete,
} from '@mui/icons-material';
import { kycAPI, uploadAPI, SERVER_URL } from '../services/api';
import ConfirmDialog from './ConfirmDialog';

const DOC_TYPES = [
  { value: 'aadhaar', label: 'Aadhaar Card' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'other', label: 'Other' },
];

const STATUS_CFG = {
  pending:  { label: 'Pending',  color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', icon: <Description sx={{ fontSize: 14 }} /> },
  verified: { label: 'Verified', color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  rejected: { label: 'Rejected', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', icon: <Cancel sx={{ fontSize: 14 }} /> },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function KycSection({ userId, gymId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [docType, setDocType] = useState('aadhaar');
  const [docNumber, setDocNumber] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Preview dialog
  const [previewUrl, setPreviewUrl] = useState(null);

  // Reject dialog
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Delete confirm dialog
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

  const loadDocs = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await kycAPI.getForUser(userId);
      setDocs(res.data.data || []);
    } catch (e) {
      console.error('Failed to load KYC docs:', e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleUpload = async () => {
    if (!file) { setAlert({ type: 'error', msg: 'Please select a file' }); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', userId);
      formData.append('docType', docType);
      if (docNumber) formData.append('docNumber', docNumber);
      if (gymId) formData.append('gymId', gymId);

      await kycAPI.upload(formData);
      setAlert({ type: 'success', msg: 'Document uploaded successfully' });
      setUploadOpen(false);
      setFile(null);
      setDocType('aadhaar');
      setDocNumber('');
      loadDocs();
    } catch (e) {
      setAlert({ type: 'error', msg: e.response?.data?.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const handleVerify = async (id) => {
    try {
      await kycAPI.verify(id);
      setAlert({ type: 'success', msg: 'Document verified' });
      loadDocs();
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to verify' });
    }
  };

  const handleReject = async () => {
    try {
      await kycAPI.reject(rejectId, rejectReason || 'Rejected by admin');
      setAlert({ type: 'success', msg: 'Document rejected' });
      setRejectId(null);
      setRejectReason('');
      loadDocs();
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to reject' });
    }
  };

  const handleDelete = async (id) => {
    setDeleteConfirm({ open: true, id });
  };

  const doDelete = async () => {
    try {
      await kycAPI.delete(deleteConfirm.id);
      setAlert({ type: 'success', msg: 'Document deleted' });
      loadDocs();
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to delete' });
    } finally {
      setDeleteConfirm({ open: false, id: null });
    }
  };

  const resolveFileUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${SERVER_URL}${url}`;
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mt: 2.5, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <VerifiedUser sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary' }}>
            KYC Documents
          </Typography>
          {docs.length > 0 && (
            <Chip label={docs.length} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
          )}
        </Stack>
        <Button size="small" variant="outlined" startIcon={<Upload sx={{ fontSize: 14 }} />}
          onClick={() => setUploadOpen(true)} sx={{ fontSize: '0.75rem' }}>
          Upload
        </Button>
      </Stack>
      <Divider sx={{ mb: 2 }} />

      {alert && <Alert severity={alert.type} sx={{ mb: 1.5, fontSize: '0.8rem' }} onClose={() => setAlert(null)}>{alert.msg}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : docs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No documents uploaded yet
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {docs.map(doc => {
            const cfg = STATUS_CFG[doc.status] || STATUS_CFG.pending;
            return (
              <Paper key={doc._id} variant="outlined" sx={{
                p: 1.5, borderRadius: 1.5, borderColor: cfg.border + '80',
                transition: 'border-color 0.15s',
                '&:hover': { borderColor: cfg.border },
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box sx={{
                      width: 32, height: 32, borderRadius: 1.5, bgcolor: cfg.bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {cfg.icon}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8125rem' }}>
                        {DOC_TYPES.find(d => d.value === doc.docType)?.label || doc.docType}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {doc.docNumber && (
                          <Typography variant="caption" color="text.secondary">
                            {doc.docNumber}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.disabled">
                          {fmtDate(doc.createdAt)}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip label={cfg.label} size="small" sx={{
                      bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                      fontWeight: 700, fontSize: '0.65rem', height: 22,
                    }} />
                    <Tooltip title="View document">
                      <IconButton size="small" onClick={() => setPreviewUrl(resolveFileUrl(doc.fileUrl))}
                        sx={{ color: 'text.secondary' }}>
                        <Visibility sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    {doc.status === 'pending' && (
                      <>
                        <Tooltip title="Verify">
                          <IconButton size="small" onClick={() => handleVerify(doc._id)}
                            sx={{ color: '#16A34A' }}>
                            <CheckCircle sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton size="small" onClick={() => { setRejectId(doc._id); setRejectReason(''); }}
                            sx={{ color: '#DC2626' }}>
                            <Cancel sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(doc._id)}
                        sx={{ color: 'text.secondary', '&:hover': { color: '#DC2626' } }}>
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                {doc.status === 'rejected' && doc.rejectReason && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', pl: 5.5 }}>
                    Reason: {doc.rejectReason}
                  </Typography>
                )}
                {doc.status === 'verified' && doc.verifiedBy && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', pl: 5.5 }}>
                    Verified by {doc.verifiedBy.name} on {fmtDate(doc.verifiedAt)}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontSize: '1rem' }}>Upload KYC Document</Typography>
            <IconButton size="small" onClick={() => setUploadOpen(false)}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField select fullWidth label="Document Type" value={docType}
              onChange={e => setDocType(e.target.value)}>
              {DOC_TYPES.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
            </TextField>
            <TextField fullWidth label="Document Number (optional)" value={docNumber}
              onChange={e => setDocNumber(e.target.value)}
              placeholder="e.g. XXXX-XXXX-1234" />
            <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}
              sx={{ py: 1.5, borderStyle: 'dashed' }}>
              {file ? file.name : 'Choose File (PDF, JPG, PNG)'}
              <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={e => setFile(e.target.files[0])} />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewUrl} onClose={() => setPreviewUrl(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontSize: '1rem' }}>Document Preview</Typography>
            <IconButton size="small" onClick={() => setPreviewUrl(null)}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', p: 2 }}>
          {previewUrl && (
            previewUrl.match(/\.pdf$/i) ? (
              <iframe src={previewUrl} width="100%" height="500" title="Document preview"
                style={{ border: 'none', borderRadius: 8 }} />
            ) : (
              <img src={previewUrl} alt="Document" style={{ maxWidth: '100%', maxHeight: 500, borderRadius: 8 }} />
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectId} onClose={() => setRejectId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Document</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Reason for rejection" multiline rows={2}
            value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            sx={{ mt: 1 }} placeholder="e.g. Blurry image, expired document" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>Reject</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete Document"
        message="This will permanently delete this KYC document."
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: null })}
      />
    </Paper>
  );
}
